import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { getHorizontalLogo, getVerticalLogo } from './svgComponents';
import { HumiLogoLabelOrientation, HumiLogoVariant } from './types';

export * from './types';

@customElement('humi-logo')
export class HumiLogo extends LitElement {
    /**
     * The color variations of the logo
     */
    @property()
    variant: HumiLogoVariant = 'color-auto';

    /**
     * The orientation of the label if present. If undefined, this property will result in the symbol only
     */
    @property()
    labelOrientation?: HumiLogoLabelOrientation;

    render() {
        // Explicitly sets the font color in the logo if the consumer has chosen an explicit white/black logo
        if (this.variant.includes('black')) {
            this.style.setProperty('--humi-logo-font-color', 'var(--humi-logo-font-color-dark)');
        } else if (this.variant.includes('white')) {
            this.style.setProperty('--humi-logo-font-color', 'var(--humi-logo-font-color-light)');
        } else {
            // If the consumer is using the auto coloring then we remove the explicit styling so that the value is instead pulled from the root stylesheet
            this.style.removeProperty('--humi-logo-font-color');
        }

        // Get appropriate viewBox based on labelOrientation
        const viewBox = DIMENSIONS[this.labelOrientation || 'default'];

        // Handle horizontal label orientation
        if (this.labelOrientation === 'horizontal') {
            return html`${getHorizontalLogo(viewBox)}`;
        }

        // Handle vertical label orientation
        if (this.labelOrientation === 'vertical') {
            return html`${getVerticalLogo(viewBox)}`;
        }

        // Handle default orientation
        return html`<svg
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            width="100%"
            height="100%"
            fill="none"
            viewBox="${DIMENSIONS[this.labelOrientation || 'default']}"
        >
            <g>
                <path
                    fill="var(--humi-logo-font-color)"
                    data-testid="eh-logo"
                    d="M60.392 49.184c1.203 0 2.233.687 2.835 1.718s.515 2.148-.086 3.179v.086c-4.123 6.529-10.738 11.253-18.126 13.057-2.062.516-3.436 2.32-3.436 4.467a4.436 4.436 0 0 0 4.467 4.468c.343 0 .687 0 1.03-.086 8.333-1.976 15.807-6.787 21.22-13.488 5.497-6.872 8.418-15.205 8.418-24.053s-2.92-17.181-8.419-24.054C62.97 7.606 55.495 2.795 47.163.82c-.344-.086-.688-.086-1.031-.086-2.492 0-4.468 2.062-4.468 4.467a4.61 4.61 0 0 0 3.437 4.468c7.388 1.804 14.002 6.528 18.126 13.057l.086.086c.601.945.601 2.148 0 3.179-.602 1.03-1.718 1.718-2.835 1.718-.516 0-.945-.086-1.375-.344-1.632-.773-3.35-1.117-5.154-1.117-6.443 0-11.769 5.069-12.199 11.512v1.2879999999999998c.344 6.443 5.756 11.512 12.199 11.512 1.804 0 3.522-.43 5.154-1.117.344-.172.86-.258 1.289-.258M30.668.733c-.343 0-.687 0-1.03.086C21.39 2.795 13.83 7.606 8.417 14.306 2.922 21.18 0 29.513 0 38.36s2.92 17.181 8.419 24.054c5.412 6.7 12.886 11.511 21.218 13.487.344.086.688.086 1.031.086 2.492 0 4.467-2.062 4.467-4.467 0-2.148-1.374-3.952-3.436-4.468-7.388-1.803-14.002-6.528-18.126-13.057v-.086c-.601-1.03-.601-2.148-.086-3.178.601-1.031 1.718-1.719 2.835-1.719.515 0 .945.086 1.375.344 1.632.773 3.35 1.117 5.154 1.117 6.443 0 11.769-5.069 12.199-11.512v-1.3739999999999999c-.344-6.443-5.756-11.511-12.2-11.511-1.803 0-3.521.43-5.153 1.116-.43.172-.945.344-1.375.344-1.203 0-2.32-.687-2.835-1.718-.601-1.031-.515-2.148 0-3.179C17.611 16.11 24.14 11.386 31.613 9.582c2.062-.516 3.437-2.32 3.437-4.467.085-2.32-1.89-4.382-4.382-4.382"
                />
            </g>
        </svg>`;
    }
}

// Due to the presence of a label or not, the viewbox of the SVG has to be adjusted
const DIMENSIONS: Record<HumiLogoLabelOrientation | 'default', string> = {
    default: '0 0 77 77',
    horizontal: '28.98 224.31 442.03 51.36',
    vertical: '38.6 138.19 422.8 223.62',
};
